// The Bookings tab's arithmetic: its five headline figures, the table's lines
// and columns, the file it exports, and which figures open their tickets. The
// figures are the server's (internal/report/bookings.go); only shares are made here.

import { monthName } from '../lib/i18n/format'
import { type StaffKey, type StaffLanguage, type Vars } from '../lib/staffText'
import type { BookingBy, BookingCounts, BookingReport, Durations } from '../lib/types'
import { drillFilters } from './drill'
import { DefaultBands } from './insightBands'
import { calendarKeys, duration, figuresSheet, percent, shiftDays, type Fmt } from './insightCalc'
import { memberLabel } from './insightDims'
import type { TicketFilters } from './ticketReport'
import type { XSheet } from './xlsx'

type Say = (k: StaffKey, vars?: Vars) => string

/** report.DefaultOnTimeMin: on time is ± this many minutes unless the report names its own band. */
export const ON_TIME_MIN = 5

/** The ± minutes a report split early, on time and late by: the policy's, else the default. */
export const onTimeBand = (r: Pick<BookingReport, 'onTimeMin'> | null) => r?.onTimeMin ?? ON_TIME_MIN

/** The licence module for Online booking (licence.FeatureAppointments). */
export const BOOKING_MODULE = 'appointments'

export const BOOKING_BYS: readonly BookingBy[] = ['service', 'day', 'week', 'weekday', 'hour', 'source']
export const BY_KEY: Record<BookingBy, StaffKey> = {
  service: 'insight.dim.service',
  day: 'insight.dim.day',
  week: 'insight.dim.week',
  weekday: 'insight.dim.weekday',
  hour: 'insight.dim.hour',
  source: 'booking.sourceLabel',
}
const SOURCE_KEY: Record<string, StaffKey> = {
  import: 'bookingsReport.import',
  online: 'booking.source.online',
  reception: 'booking.source.reception',
}
export const LEAD_KEYS: readonly StaffKey[] = ['bookingsReport.lead.sameDay', 'bookingsReport.lead.nextDay', 'bookingsReport.lead.week', 'bookingsReport.lead.more']
export const CAME_PARTS: readonly { key: 'early' | 'onTime' | 'late'; label: StaffKey }[] = [
  { key: 'early', label: 'bookingsReport.early' },
  { key: 'onTime', label: 'bookingsReport.onTime' },
  { key: 'late', label: 'bookingsReport.late' },
]

/** The bookings whose time came and were not cancelled: what Came and Missed the booking are shares of. */
export const due = (c: BookingCounts) => c.booked - c.pending - c.cancelled
const shareText = (n: number, of: number) => percent(n / of)

/** Moves were counted over the whole period, part of it, or none of it. `since` is
 *  the server's movesSince: the day this installation began recording them
 *  (migration 0079). Unknown (an older server) reads as all. */
export function movesCounted(range: { from: string; to: string }, since: string | undefined): 'all' | 'part' | 'none' {
  if (!since) return 'all'
  return range.to < since ? 'none' : range.from < since ? 'part' : 'all'
}
const movesFromVars = (since: string, lang: StaffLanguage) => ({ date: `${Number(since.slice(8))} ${monthName(lang, Number(since.slice(5, 7)) - 1)}` })

/** How the median arrival stood against the booked time, in words: "3 min early",
 *  "2 min late", "on the booked time"; a dash when nobody came. */
export function latenessText(c: BookingCounts, lang: StaffLanguage, say: Say): string {
  if (c.lateness.count === 0) return '—'
  const m = c.lateness.medianSec
  if (m === 0) return say('bookingsReport.onTheDot')
  return say(m < 0 ? 'bookingsReport.earlyBy' : 'bookingsReport.lateBy', { time: duration(Math.abs(m), lang) })
}

export type Tile = {
  key: 'booked' | 'came' | 'missed' | 'cancelled' | 'moved'
  label: StaffKey
  /** Null where nothing was counted: Moved before moves were recorded. */
  value: number | null
  /** The lines under the figure, each share with its own base written out ("71% of 7 due"). */
  notes: { key: StaffKey; vars: Vars }[]
  /** Whether the figure opens its tickets: only bookings that came have one. */
  drills?: boolean
}

/** Booked, Came (and how many on time), Missed the booking, Cancelled, Moved. Every share
 *  says what it is a share of, because they are not of one base and do not add up. */
export function bookingTiles(r: BookingCounts, range: { from: string; to: string }, movesSince: string | undefined, lang: StaffLanguage): Tile[] {
  const d = due(r)
  const ofDue = (n: number) => (d > 0 ? [{ key: 'bookingsReport.shareOfDue' as StaffKey, vars: { share: shareText(n, d), n: d } }] : [])
  const ofBooked = (n: number) => (r.booked > 0 ? [{ key: 'bookingsReport.shareOfBooked' as StaffKey, vars: { share: shareText(n, r.booked), n: r.booked } }] : [])
  const moves = movesCounted(range, movesSince)
  return [
    { key: 'booked', label: 'bookingsReport.booked', value: r.booked, notes: r.pending > 0 ? [{ key: 'bookingsReport.toCome', vars: { n: r.pending } }] : [] },
    {
      key: 'came',
      label: 'bookingsReport.came',
      value: r.arrived,
      // Early, on time and late together: "on time" alone read as poor when most came early.
      notes: [...ofDue(r.arrived), ...(r.arrived > 0 ? [{ key: 'bookingsReport.arrivalSplit' as StaffKey, vars: { early: r.early, onTime: r.onTime, late: r.late } }] : [])],
      drills: true,
    },
    { key: 'missed', label: 'bookingsReport.missed', value: r.missed, notes: ofDue(r.missed) },
    { key: 'cancelled', label: 'bookingsReport.cancelled', value: r.cancelled, notes: ofBooked(r.cancelled) },
    {
      key: 'moved',
      label: 'bookingsReport.moved',
      value: moves === 'none' ? null : r.moved,
      notes: moves === 'all' || !movesSince ? ofBooked(r.moved) : [{ key: 'bookingsReport.movedFrom', vars: movesFromVars(movesSince, lang) }],
    },
  ]
}

/** The tab is there when the licence has Online booking, or the branch had a booking in the period. */
export function showsBookings(licence: readonly string[] | undefined, last: Pick<BookingCounts, 'booked'> | null): boolean {
  return (licence ?? []).includes(BOOKING_MODULE) || (last?.booked ?? 0) > 0
}

const ZERO: Durations = { count: 0, meanSec: 0, medianSec: 0, p90Sec: 0, maxSec: 0 }
/** A period with no bookings at all. */
export const noBookings = (): BookingCounts => ({
  booked: 0,
  arrived: 0,
  early: 0,
  onTime: 0,
  late: 0,
  missed: 0,
  cancelled: 0,
  moved: 0,
  pending: 0,
  lateness: ZERO,
  leadTime: ZERO,
  lead: [0, 0, 0, 0],
  waited: ZERO,
  serviceLevel: { measured: 0, within: 0 },
})

export type BookingLine = { key: string; label: string; c: BookingCounts }

/** The table's lines in the reader's words: days and weeks as the Figures tab names
 *  them, a week the period cuts by its covered dates. */
export function bookingLines(r: BookingReport, lang: StaffLanguage, say: Say): BookingLine[] {
  const label = (key: string, server: string) => {
    switch (r.by) {
      case 'day':
      case 'week':
      case 'weekday':
        return memberLabel(r.by, key, lang, say, r.range)
      case 'source': {
        const k = SOURCE_KEY[key]
        return k ? say(k) : key
      }
      default:
        return server || key || '—'
    }
  }
  const lines: BookingLine[] = (r.rows ?? []).map((row) => ({ key: row.key, label: label(row.key, row.name || row.label), c: row }))
  // Every day, week and weekday of the period is a row, an empty one as zero, as on Figures.
  if (r.by !== 'day' && r.by !== 'week' && r.by !== 'weekday') return lines
  const have = new Set(lines.map((l) => l.key))
  for (const key of calendarKeys(r.by, r.range.from, r.range.to) ?? []) if (!have.has(key)) lines.push({ key, label: label(key, ''), c: noBookings() })
  return lines.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0))
}

/** The median wait after the booked time, and the service level; null where nothing was measured. */
export const waitedMedian = (c: BookingCounts) => (c.waited.count > 0 ? c.waited.medianSec : null)
export const levelOf = (c: BookingCounts) => (c.serviceLevel.measured > 0 ? c.serviceLevel.within / c.serviceLevel.measured : null)

export type BookingColumn = { label: StaffKey; fmt: Fmt; of: (c: BookingCounts) => number | null; drills?: true }

export const BOOKING_COLUMNS: readonly BookingColumn[] = [
  { label: 'bookingsReport.booked', fmt: 'int', of: (c) => c.booked },
  { label: 'bookingsReport.came', fmt: 'int', of: (c) => c.arrived, drills: true },
  { label: 'bookingsReport.early', fmt: 'int', of: (c) => c.early },
  { label: 'bookingsReport.onTime', fmt: 'int', of: (c) => c.onTime },
  { label: 'bookingsReport.late', fmt: 'int', of: (c) => c.late },
  { label: 'bookingsReport.missed', fmt: 'int', of: (c) => c.missed },
  { label: 'bookingsReport.cancelled', fmt: 'int', of: (c) => c.cancelled },
  { label: 'bookingsReport.moved', fmt: 'int', of: (c) => c.moved },
  { label: 'reports.waitingMedian', fmt: 'dur', of: waitedMedian },
  { label: 'reports.serviceLevel', fmt: 'pct', of: levelOf },
]

/** A column's figure on a line: Moved is a dash where moves were not yet recorded,
 *  judged by the line's own dates for a day or a week, else by the period's. */
export function columnValue(col: BookingColumn, c: BookingCounts, r: Pick<BookingReport, 'range' | 'movesSince' | 'by'>, key?: string): number | null {
  if (col.label !== 'bookingsReport.moved') return col.of(c)
  const dated = key && /^\d{4}-\d{2}-\d{2}$/.test(key) && (r.by === 'day' || r.by === 'week')
  const span = dated ? { from: key, to: r.by === 'week' ? shiftDays(key, 6) : key } : r.range
  return movesCounted(span, r.movesSince) === 'none' ? null : col.of(c)
}

/** The table as a worksheet: its lines, then the total. */
export function bookingsSheet(r: BookingReport, lines: BookingLine[], title: string, about: string, byName: string, say: Say, minutes: string): XSheet {
  const cells = (c: BookingCounts, key?: string) => BOOKING_COLUMNS.map((col) => columnValue(col, c, r, key))
  return figuresSheet(
    title,
    about,
    [byName, ...BOOKING_COLUMNS.map((col) => say(col.label))],
    [...lines.map((l) => [l.label, ...cells(l.c, l.key)] as [string, ...(number | null)[]]), [say('insight.total'), ...cells(r)]],
    BOOKING_COLUMNS.map((col) => col.fmt),
    minutes,
  )
}

const ALONE = { rows: [], cols: [], values: [] }

/**
 * The tickets of the bookings that came, by the server's link from booking to
 * ticket (booked): a service row is its booked service, a day, week or weekday
 * its dates. Null where the ticket does not carry the row: the booked hour is not
 * the hour the ticket was taken, and the booking's source is not on the ticket.
 */
export function bookingDrill(base: { branchId: string; from: string; to: string }, by: BookingBy | null, key = ''): TicketFilters | null {
  if (by === 'hour' || by === 'source' || (by && by !== 'service' && !key)) return null
  const plain: TicketFilters = { branches: [base.branchId], from: base.from, to: base.to, service: '', staff: '', result: '', channel: '' }
  if (by === 'service') return { ...plain, booked: key || 'none' }
  const f = by ? drillFilters({ ...base, before: base }, { spot: { period: 'now', dim: by, key }, measure: 'arrived' }, { ...ALONE, rows: [by] }, DefaultBands) : plain
  return f && { ...f, booked: 'all' }
}
